import { useState, useEffect } from 'react'
import { ListChecks, FlaskConical, Check, HelpCircle, Beaker as BeakerIcon } from 'lucide-react'
import { useLab } from '../../context/LabContext'
import { Beaker, Dropper, LitmusPaper, StirringRod, SoilContainer, Timer } from '../lab'
import InfoBox from './InfoBox'

const steps = [
  { id: 1, title: 'Label Beakers', short: 'Click beakers to label' },
  { id: 2, title: 'Add Water', short: 'Click experimental beaker' },
  { id: 3, title: 'Add Soil (10.0 g)', short: 'Click soil container' },
  { id: 4, title: 'Stir', short: 'Click stirring rod' },
  { id: 5, title: 'Pour Half', short: 'Click experimental beaker' },
  { id: 6, title: 'Add Lime', short: 'Add 1 dropper full of lime solution' },
  { id: 7, title: 'Test pH', short: 'Click litmus paper' },
  { id: 8, title: 'Add Alum', short: 'Add 1 dropper full; continue until thick precipitate forms' },
  { id: 9, title: 'Settle', short: 'Start timer and wait' },
  { id: 10, title: 'Compare', short: 'Observe results' }
]

const Part1Purification = () => {
  const { state, updatePart1, completeStep } = useLab()
  const { exp2Part1, currentStep, labMode, completedSteps } = state

  const [showInfo, setShowInfo] = useState(null)
  const [dropping, setDropping] = useState(null)
  const [stirring, setStirring] = useState(false)
  const [dipping, setDipping] = useState(false)
  const [timerRunning, setTimerRunning] = useState(false)
  const [timerValue, setTimerValue] = useState(0)
  const [showReaction, setShowReaction] = useState(null)

  const isActive = (s) => labMode === 'free' || currentStep === s
  const isDone = (s) => completedSteps.includes(`part1-${s}`)

  // Timer effect
  useEffect(() => {
    if (!timerRunning || timerValue >= 60) return
    const t = setInterval(() => {
      setTimerValue(v => {
        const newVal = v + 1
        const progress = Math.min(newVal / 60, 1)
        updatePart1({ settlingProgress: progress })
        if (progress >= 1) {
          updatePart1({
            beakers: {
              ...exp2Part1.beakers,
              experimental: { ...exp2Part1.beakers.experimental, cleared: true }
            }
          })
          setTimerRunning(false)
          completeStep('part1-9')
          completeStep('part1-10')
        }
        return newVal
      })
    }, 50) // Fast for demo
    return () => clearInterval(t)
  }, [timerRunning, timerValue])

  // Handlers
  const handleLabel = () => {
    if (!isActive(1)) return
    updatePart1({ labeled: true })
    completeStep('part1-1')
  }

  const handleWater = () => {
    if (!isActive(2) || !exp2Part1.labeled) return
    updatePart1({
      beakers: {
        ...exp2Part1.beakers,
        experimental: { ...exp2Part1.beakers.experimental, water: 75 }
      }
    })
    completeStep('part1-2')
  }

  const handleSoil = () => {
    if (!isActive(3) || !exp2Part1.beakers.experimental.water) return
    updatePart1({
      beakers: {
        ...exp2Part1.beakers,
        experimental: { ...exp2Part1.beakers.experimental, muddy: true }
      }
    })
    completeStep('part1-3')
  }

  const handleStir = () => {
    if (!isActive(4) || !exp2Part1.beakers.experimental.muddy) return
    setStirring(true)
    setTimeout(() => {
      setStirring(false)
      updatePart1({ stirred: true })
      completeStep('part1-4')
    }, 1500)
  }

  const handlePour = () => {
    if (!isActive(5) || !exp2Part1.stirred) return
    updatePart1({
      beakers: {
        control: { water: 37, muddy: true },
        experimental: { ...exp2Part1.beakers.experimental, water: 38 }
      },
      poured: true
    })
    completeStep('part1-5')
  }

  const handleLime = () => {
    if (!isActive(6) || !exp2Part1.poured) return
    setDropping('lime')
    setTimeout(() => {
      setDropping(null)
      const newLime = (exp2Part1.beakers.experimental.lime || 0) + 1
      updatePart1({
        beakers: {
          ...exp2Part1.beakers,
          experimental: {
            ...exp2Part1.beakers.experimental,
            lime: newLime,
            basic: newLime >= 3
          }
        }
      })
      if (newLime >= 3) {
        setShowReaction('lime')
        completeStep('part1-6')
      }
    }, 400)
  }

  const handleLitmus = () => {
    if (!isActive(7) || !exp2Part1.beakers.experimental.basic) return
    setDipping(true)
    setTimeout(() => {
      setDipping(false)
      updatePart1({ litmusColor: 'blue' })
      completeStep('part1-7')
    }, 1000)
  }

  const handleAlum = () => {
    if (!isActive(8) || exp2Part1.litmusColor !== 'blue') return
    setDropping('alum')
    setTimeout(() => {
      setDropping(null)
      const newAlum = (exp2Part1.beakers.experimental.alum || 0) + 1
      updatePart1({
        beakers: {
          ...exp2Part1.beakers,
          experimental: {
            ...exp2Part1.beakers.experimental,
            alum: newAlum,
            precipitate: Math.min(newAlum * 15, 60)
          }
        }
      })
      if (newAlum >= 4) {
        setShowReaction('alum')
        completeStep('part1-8')
      }
    }, 400)
  }

  const handleTimer = () => {
    if (!isActive(9) || exp2Part1.beakers.experimental.precipitate < 50) return
    setTimerRunning(true)
  }

  const completedCount = completedSteps.filter(s => s.startsWith('part1')).length

  return (
    <div className="part1">
      {/* Steps Panel */}
      <div className="steps-panel">
        <div className="steps-header">
          <ListChecks size={16} />
          <h3>Part I: Purification</h3>
        </div>
        <div className="steps-list">
          {steps.map(s => (
            <div
              key={s.id}
              className={`step-item ${isActive(s.id) && !isDone(s.id) ? 'active' : ''}`}
            >
              <div className={`step-number ${isDone(s.id) ? 'done' : isActive(s.id) ? 'current' : ''}`}>
                {isDone(s.id) ? <Check size={12} /> : s.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className={`step-title ${isActive(s.id) ? 'highlight' : ''}`}>
                    {s.title}
                  </span>
                  <button
                    className="step-info-btn"
                    onClick={(e) => { e.stopPropagation(); setShowInfo(s.id) }}
                  >
                    ?
                  </button>
                </div>
                {isActive(s.id) && !isDone(s.id) && (
                  <span className="step-hint">{s.short}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lab Bench */}
      <div className="lab-bench">
        <div className="bench-header">
          <div className="bench-title">
            <FlaskConical size={16} />
            <h3>Lab Bench</h3>
          </div>
          <span className="bench-progress">{completedCount}/10 Complete</span>
        </div>

        <div className="bench-area">
          {/* Progress bar for settling */}
          {exp2Part1.settlingProgress > 0 && (
            <div className="settling-progress">
              <div
                className="settling-bar"
                style={{ width: `${exp2Part1.settlingProgress * 100}%` }}
              />
              <span>{Math.round(exp2Part1.settlingProgress * 100)}%</span>
            </div>
          )}

          {/* Equipment Layout */}
          <div className="equipment-grid">
            <div className="beakers-section">
              <Beaker
                size={500}
                fillLevel={exp2Part1.beakers.control.water}
                muddy={exp2Part1.beakers.control.muddy}
                label={exp2Part1.labeled ? 'Control' : 'Beaker 1'}
                onClick={handleLabel}
                active={isActive(1) && !exp2Part1.labeled}
                disabled={labMode === 'guided' && currentStep !== 1}
              />
              <Beaker
                size={500}
                fillLevel={exp2Part1.beakers.experimental.water}
                muddy={exp2Part1.beakers.experimental.muddy}
                cleared={exp2Part1.beakers.experimental.cleared}
                precipitate={exp2Part1.beakers.experimental.precipitate / 100}
                label={exp2Part1.labeled ? 'Experimental' : 'Beaker 2'}
                onClick={currentStep === 2 ? handleWater : currentStep === 5 ? handlePour : handleLabel}
                active={isActive(1) || isActive(2) || isActive(5)}
                disabled={labMode === 'guided' && ![1, 2, 5].includes(currentStep)}
              />
            </div>

            <div className="tools-section">
              <SoilContainer
                onClick={handleSoil}
                active={isActive(3)}
                disabled={labMode === 'guided' && currentStep !== 3}
              />
              <StirringRod
                onClick={handleStir}
                active={isActive(4)}
                disabled={labMode === 'guided' && currentStep !== 4}
                stirring={stirring}
              />
              <Dropper
                label="Lime"
                liquidColor="#FEF3C7"
                onClick={handleLime}
                active={isActive(6)}
                disabled={labMode === 'guided' && currentStep !== 6}
                dropping={dropping === 'lime'}
              />
              <LitmusPaper
                color={exp2Part1.litmusColor}
                onClick={handleLitmus}
                active={isActive(7)}
                disabled={labMode === 'guided' && currentStep !== 7}
                dipping={dipping}
              />
              <Dropper
                label="Alum"
                liquidColor="#E2E8F0"
                onClick={handleAlum}
                active={isActive(8)}
                disabled={labMode === 'guided' && currentStep !== 8}
                dropping={dropping === 'alum'}
              />
              <Timer
                time={timerValue}
                running={timerRunning}
                onClick={handleTimer}
              />
            </div>
          </div>

          {/* Bench surface */}
          <div className="bench-surface" />
        </div>
      </div>

      {/* Reaction Modal */}
      {showReaction && (
        <div className="reaction-overlay" onClick={() => setShowReaction(null)}>
          <div className="reaction-modal" onClick={e => e.stopPropagation()}>
            <div className="reaction-icon">
              <BeakerIcon size={28} />
            </div>
            <h3>Chemical Reaction!</h3>
            <div className="reaction-equation">
              {showReaction === 'lime'
                ? <>CaO + H₂O → Ca²⁺ + 2OH⁻</>
                : <>Al³⁺ + 3OH⁻ → Al(OH)₃↓</>
              }
            </div>
            <p>
              {showReaction === 'lime'
                ? 'Lime creates a basic solution.'
                : 'Aluminum hydroxide precipitates, carrying impurities.'}
            </p>
            <button onClick={() => setShowReaction(null)}>Continue</button>
          </div>
        </div>
      )}

      {/* Info Box */}
      {showInfo && <InfoBox step={showInfo} experimentId="exp2" onClose={() => setShowInfo(null)} />}

      <style>{`
        .part1 {
          display: flex;
          gap: 16px;
          height: 100%;
          padding: 16px;
        }

        .steps-panel {
          width: 240px;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .steps-header {
          background: linear-gradient(135deg, var(--primary), var(--primary-dark));
          padding: 14px 16px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: white;
        }

        .steps-header h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .steps-list {
          flex: 1;
          overflow-y: auto;
          padding: 12px;
        }

        .step-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 8px;
          margin-bottom: 4px;
          transition: background 0.15s;
        }

        .step-item.active {
          background: rgba(255, 90, 54, 0.15);
        }

        .step-number {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
          flex-shrink: 0;
          background: var(--border);
          color: var(--text-muted);
        }

        .step-number.done {
          background: var(--secondary);
          color: white;
        }

        .step-number.current {
          background: var(--primary);
          color: white;
        }

        .step-content {
          flex: 1;
          min-width: 0;
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-title {
          font-size: 12px;
          font-weight: 600;
        }

        .step-title.highlight {
          color: var(--primary-light);
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }

        .step-info-btn:hover {
          transform: scale(1.1);
        }

        .step-hint {
          font-size: 11px;
          color: var(--text-secondary);
          margin-top: 4px;
          display: block;
        }

        .lab-bench {
          flex: 1;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: auto;
        }

        .bench-header {
          background: linear-gradient(135deg, var(--secondary), var(--secondary-dark));
          padding: 12px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: white;
        }

        .bench-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .bench-title h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .bench-progress {
          font-size: 12px;
          opacity: 0.9;
        }

        .bench-area {
          flex: 1;
          background: linear-gradient(180deg, var(--bg-elevated) 0%, var(--bg-surface) 100%);
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .settling-progress {
          position: absolute;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          width: 200px;
          height: 24px;
          background: var(--border);
          border-radius: 12px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .settling-bar {
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          background: var(--secondary);
          transition: width 0.3s;
        }

        .settling-progress span {
          position: relative;
          font-size: 12px;
          font-weight: 600;
          color: white;
          text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        }

        .equipment-grid {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 30px;
        }

        .beakers-section {
          display: flex;
          gap: 60px;
          align-items: flex-end;
        }

        .tools-section {
          display: flex;
          gap: 24px;
          align-items: flex-end;
        }

        .bench-surface {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 60px;
          background: linear-gradient(180deg, #5C534A 0%, #3D352F 100%);
          border-top: 4px solid #4A423B;
        }

        .reaction-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .reaction-modal {
          background: var(--bg-surface);
          border-radius: 16px;
          padding: 32px;
          max-width: 380px;
          text-align: center;
          border: 1px solid var(--border);
        }

        .reaction-icon {
          width: 56px;
          height: 56px;
          background: rgba(0, 168, 107, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          color: var(--secondary);
        }

        .reaction-modal h3 {
          font-size: 18px;
          margin-bottom: 16px;
        }

        .reaction-equation {
          background: var(--bg-elevated);
          border-radius: 10px;
          padding: 16px;
          font-family: 'Courier New', monospace;
          font-size: 16px;
          color: var(--accent);
          margin-bottom: 16px;
        }

        .reaction-modal p {
          color: var(--text-secondary);
          font-size: 14px;
          margin-bottom: 20px;
        }

        .reaction-modal button {
          padding: 12px 32px;
          background: var(--primary);
          color: white;
          border-radius: 8px;
          font-weight: 600;
        }
      `}</style>
    </div>
  )
}

export default Part1Purification
